/**
 * Menu is component that shows extra content when it is clicked.
 * Clicking it toggles visibility of MenuContent.
 *
 * Example:
 *  <Menu>
 *    <MenuLabel>
 *      <span>Open menu</span>
 *    </MenuLabel>
 *    <MenuContent>
 *      <MenuItem key="first item">
 *        <Button onClick={onClick}>Click this</Button>
 *      </MenuItem>
 *    </MenuContent>
 *  </Menu>
 *
 */

import React, { useState, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';

import MenuContent from '../MenuContent/MenuContent';
import MenuLabel from '../MenuLabel/MenuLabel';
import css from './Menu.module.css';

const KEY_CODE_ESCAPE = 27;
const CONTENT_PLACEMENT_OFFSET = 0;
const CONTENT_TO_LEFT = 'left';
const CONTENT_TO_RIGHT = 'right';
const MAX_MOBILE_SCREEN_WIDTH = 767;

const isControlledMenu = (isOpenProp, onToggleActiveProp) => {
  return isOpenProp !== null && onToggleActiveProp !== null;
};

function Menu(props) {
  const {
      className = null,
      rootClassName = '',
      contentPlacementOffset = CONTENT_PLACEMENT_OFFSET,
      contentPosition = CONTENT_TO_RIGHT,
      isOpen: isOpenProp = null,
      onToggleActive = null,
      useArrow = true,
      children,
  } = props;
  const [isOpen, setIsOpen] = useState(isOpenProp ?? false);
  const [ready, setReady] = useState(false);
  const menuRef = useRef(null);
  const menuContentRef = useRef(null);

  useEffect(() => {
    // Menu needs to know about DOM before it can calculate it's size proberly.
    setReady(true);
  }, []);

  useEffect(() => {
    if (isControlledMenu(isOpenProp, onToggleActive)) {
      setIsOpen(isOpenProp);
    }
  }, [isOpenProp, onToggleActive]);

  const onBlur = event => {
    // FocusEvent is fired faster than the link elements native click handler
    // gets its own event. Therefore, we need to check the origin of this FocusEvent.
    if (!menuRef.current.contains(event.relatedTarget)) {
      // const { isOpen: controlledIsOpen, onToggleActive } = props;
      // Because defaults props are not used in new React, destructuring props directly above is safer, 
      // but variable names shadow outer scope if we do it again.
      // However, the outer scope variables 'isOpenProp' (controlledIsOpen) and 'onToggleActive' are available in closure.
      // Let's use the defaulted variables from the top scope.
      
      if (isControlledMenu(isOpenProp, onToggleActive)) {
        onToggleActive(false);
      } else {
        setIsOpen(false);
      }
    }
  };

  const onKeyDown = e => {
    // Gather all escape presses to close menu
    if (e.keyCode === KEY_CODE_ESCAPE) {
      toggleOpen(false);
    }
  };

  const toggleOpen = enforcedState => {
    // If state is handled outside of Menu component, we call a passed in onToggleActive func
    // Use the defaulted variables from closure
    if (isControlledMenu(isOpenProp, onToggleActive)) {
      const isMenuOpen = enforcedState != null ? enforcedState : !isOpenProp;
      onToggleActive(isMenuOpen);
    } else {
      // If state is handled inside of Menu component, set state
      setIsOpen(prevState => {
        const isMenuOpen = enforcedState != null ? enforcedState : !prevState;
        return isMenuOpen;
      });
    }
  };

  const positionStyleForMenuContent = contentPosition => {
    if (menuRef.current && menuContentRef.current) {
      const windowWidth = window.innerWidth;
      const rect = menuRef.current.getBoundingClientRect();

      // Calculate wether we should show the menu to the left of the component or right
      const distanceToRight = windowWidth - rect.right;
      const menuWidth = menuRef.current.offsetWidth;
      const contentWidthBiggerThanLabel = menuContentRef.current.offsetWidth - menuWidth;
      const usePositionLeftFromLabel = contentPosition === CONTENT_TO_LEFT;
      // const contentPlacementOffset = props.contentPlacementOffset;
      // Using defaulted variable

      if (windowWidth <= MAX_MOBILE_SCREEN_WIDTH) {
        // Take full screen width on mobile
        return {
          left: -1 * (rect.left - 24),
          width: 'calc(100vw - 48px)',
        };
      }

      // Render menu content to the left according to the contentPosition
      // prop or if the content does not fit to the right. Otherwise render to
      // the right.
      return usePositionLeftFromLabel || distanceToRight < contentWidthBiggerThanLabel
        ? { right: contentPlacementOffset, minWidth: menuWidth }
        : { left: contentPlacementOffset, minWidth: menuWidth };
    }

    // When the MenuContent is rendered for the first time
    // (for the sake of width calculation),
    // move it outside of viewport to prevent possible overflow.
    return isOpen ? {} : { left: '-10000px' };
  };

  const positionStyleForArrow = isPositionedRight => {
    if (menuRef.current) {
      const menuWidth = menuRef.current.offsetWidth;
       // const contentPlacementOffset = props.contentPlacementOffset;
       // Using defaulted variable
      return isPositionedRight
        ? Math.floor(menuWidth / 2) - contentPlacementOffset
        : Math.floor(menuWidth / 2);
    }
    return 0;
  };

  const prepareChildren = () => {
    if (React.Children.count(children) !== 2) {
      throw new Error('Menu needs to have two children: MenuLabel and MenuContent.');
    }

    return React.Children.map(children, child => {
      const isOpenState = isControlledMenu(isOpenProp, onToggleActive) ? isOpenProp : isOpen;

      if (child.type === MenuLabel) {
        // MenuLabel needs toggleOpen function
        // We pass that directly  so that component user doesn't need to worry about that
        return React.cloneElement(child, {
          isOpen: isOpenState,
          onToggleActive: toggleOpen,
        });
      } else if (child.type === MenuContent) {
        // MenuContent needs some styling data (width, arrowPosition, and isOpen info)
        // We pass those directly so that component user doesn't need to worry about those.
        // const { contentPosition, useArrow } = props;
        const positionStyles = positionStyleForMenuContent(contentPosition);
        const arrowPosition = useArrow ? positionStyleForArrow(positionStyles.right != null) : null;
        return React.cloneElement(child, {
          arrowPosition,
          contentRef: node => {
            menuContentRef.current = node;
          },
          isOpen: isOpenState,
          style: { ...child.props.style, ...positionStyles },
        });
      } else {
        throw new Error('Menu has an unknown child. Only MenuLabel and MenuContent are allowed.');
      }
    });
  };

  // const { className, rootClassName } = props;
  const rootClass = rootClassName || css.root;
  const classes = classNames(rootClass, className);
  const menuChildren = ready ? prepareChildren() : null;

  return (
    <div className={classes} onBlur={onBlur} tabIndex={0} onKeyDown={onKeyDown} ref={menuRef}>
      {menuChildren}
    </div>
  );
}



const { bool, func, node, number, string } = PropTypes;

Menu.propTypes = {
  children: node.isRequired,
  className: string,
  rootClassName: string,
  contentPosition: string,
  contentPlacementOffset: number,
  useArrow: bool,
  isOpen: bool,
  onToggleActive: func,
};

export default Menu;
